import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { NameInput } from "../components/UI/tools";
import { appState } from "@/app/appState";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { PublicPageShell } from "@/components/PublicPageShell";

export default observer(function InvitePage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [fullName, setFullName] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [inviteValid, setInviteValid] = useState<boolean | null>(null);
    const [validationError, setValidationError] = useState("");

    // Form validity tracking
    const [isFullNameValid, setIsFullNameValid] = useState(false);
    const [isEmailValid, setIsEmailValid] = useState(false);
    const [isPasswordValid, setIsPasswordValid] = useState(false);

    useEffect(() => {
        const checkInvite = async () => {
            if (!token) return;
            try {
                const response = await fetch(`/api/auth/invites/${token}`);
                const data = await response.json();
                if (data.valid) {
                    setInviteValid(true);
                    if (data.email) {
                        setEmail(data.email);
                        setIsEmailValid(true);
                    }
                } else {
                    setInviteValid(false);
                    setValidationError(data.reason || appState.t("auth.invalidInvite"));
                }
            } catch (e) {
                setInviteValid(false);
                setValidationError(appState.t("auth.couldNotValidateInvite"));
            }
        };
        checkInvite();
    }, [token]);

    const handleLogin = (provider: string) => {
        window.location.href = `/api/auth/login/${provider}?invite_token=${token}`;
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!isFullNameValid || !isEmailValid || !isPasswordValid) {
            setError(appState.t("auth.fillFields"));
            return;
        }

        setError("");
        setLoading(true);

        try {
            const response = await fetch("/api/auth/register-invite", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    invite_token: token,
                    email,
                    password,
                    full_name: fullName
                }),
            });

            if (response.ok) {
                window.location.href = "/workspace";
            } else {
                const data = await response.json();
                setError(data.detail || appState.t("auth.registrationFailed"));
            }
        } catch (e) {
            setError(appState.t("auth.genericError"));
        } finally {
            setLoading(false);
        }
    };

    if (inviteValid === false) {
        return (
            <PublicPageShell contentClassName="items-center justify-center p-3">
                <div className="auth-card">
                    <LanguageSwitcher />
                    <div className="flex flex-row items-center justify-center text-center h-full">{validationError}</div>
                    <button
                        onClick={() => navigate('/')}
                        className="btn btn-primary btn-block mt-4"
                    >
                        {appState.t("auth.goHome")}
                    </button>
                </div>
            </PublicPageShell>
        );
    }

    if (inviteValid === null) {
        return <></>
    }

    return (
        <PublicPageShell contentClassName="items-center justify-center p-3">
            <div className="auth-card w-fit">
                        <LanguageSwitcher />
                        <p className="auth-title text-center">{appState.t("auth.invitationTitle")}</p>
                    <div className="p-4 w-full flex flex-col gap-4">

                        <div className="flex flex-col gap-2">

                            <p className="b text-center">{appState.t("auth.createAccount")}</p>

                            <form onSubmit={handleRegister} className="flex flex-col gap-2">
                                <div>
                                    <label className="field-label">{appState.t("auth.fullName")}</label>
                                    <NameInput
                                        value={fullName}
                                        label={appState.t("auth.fullName")}
                                        type="text"
                                        onChange={(val) => setFullName(val)}
                                        validator={(val) => val.trim().length > 0}
                                        onValidChange={(key, valid) => setIsFullNameValid(valid)}
                                        edit={true}
                                    />
                                </div>

                                <div>
                                    <label className="field-label">{appState.t("common.email")}</label>
                                    <NameInput
                                        value={email}
                                        label={appState.t("common.email")}
                                        type="text"
                                        onChange={(val) => setEmail(val)}
                                        validator={(val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)}
                                        onValidChange={(key, valid) => setIsEmailValid(valid)}
                                        edit={true}
                                    />
                                </div>

                                <div>
                                    <label className="field-label">{appState.t("common.password")}</label>
                                    <NameInput
                                        value={password}
                                        label={appState.t("common.password")}
                                        type="password"
                                        onChange={(val) => setPassword(val)}
                                        validator={(val) => val.length >= 8}
                                        onValidChange={(key, valid) => setIsPasswordValid(valid)}
                                        edit={true}
                                    />
                                </div>

                                {error && <p className="error-text p-1">{error}</p>}

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="btn btn-primary btn-block"
                                >
                                    {loading ? appState.t("auth.creatingAccount") : appState.t("auth.createAccountButton")}
                                </button>
                            </form>

                        </div>
                        <div className="flex flex-col justify-center">

                            <div className="divider-label">{appState.t("common.or")}</div>
                        </div>
                        <div className="flex flex-col gap-2">
                        <p className="b text-center">{appState.t("auth.signInSso")}</p>

                            <button
                                onClick={() => handleLogin('google')}
                                className="btn btn-block"
                            >
                                {appState.t("auth.signInGoogle")}
                            </button>
                        </div>
                        
                    </div>
                </div>
        </PublicPageShell>
    );
});
